import { motion } from 'framer-motion'
import { ArrowRightIcon, PlayIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { 
  AIIcon, 
  WebDevIcon, 
  AutomationIcon, 
  CloudIcon,
  SecurityIcon,
  TransformIcon 
} from '@/components/icons/ServiceIcons'

export default function Home() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  const handleScrollToContact = () => {
    const contactSection = document.getElementById('contacto')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' })
    } else {
      // Si no existe la sección, navegar a la página de contacto
      window.location.href = '/contacto'
    }
  }

  const featuredServices = [
    {
      id: 'ai',
      title: "Inteligencia Artificial",
      description: "Desarrollo de agentes IA, machine learning y automatización inteligente para revolucionar tus procesos empresariales con tecnología de vanguardia.",
      shortDesc: "Agentes IA y automatización inteligente para procesos empresariales",
      icon: AIIcon,
      featured: true,
      category: "AI & Machine Learning",
      link: "/servicios/inteligencia-artificial"
    },
    {
      id: 'web-dev',
      title: "Desarrollo Web",
      description: "Aplicaciones web modernas, responsivas y escalables con las últimas tecnologías y mejores prácticas de desarrollo.",
      shortDesc: "Aplicaciones web modernas y escalables con tecnologías de vanguardia",
      icon: WebDevIcon,
      featured: false,
      category: "Desarrollo",
      link: "/servicios/desarrollo-web"
    },
    {
      id: 'automation',
      title: "Automatización",
      description: "Automatización de procesos empresariales, workflows inteligentes y optimización de operaciones para maximizar eficiencia.",
      shortDesc: "Automatización de procesos y workflows para optimizar operaciones",
      icon: AutomationIcon,
      featured: false,
      category: "Automatización",
      link: "/servicios/automatizacion"
    },
    {
      id: 'cloud',
      title: "Soluciones Cloud",
      description: "Migración a la nube, arquitecturas escalables y optimización de costos con proveedores líderes del mercado.",
      shortDesc: "Migración y optimización de infraestructura en la nube",
      icon: CloudIcon,
      featured: false,
      category: "Infraestructura",
      link: "/servicios/cloud"
    },
    {
      id: 'security',
      title: "Ciberseguridad",
      description: "Protección avanzada de datos, auditorías de seguridad y cumplimiento normativo para blindar tu empresa.",
      shortDesc: "Protección avanzada y auditorías de seguridad empresarial",
      icon: SecurityIcon,
      featured: false,
      category: "Seguridad",
      link: "/servicios/ciberseguridad"
    }
  ]
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              <span className="text-cyber">Transformamos</span><br />
              <span className="text-gradient">Ideas en Código</span>
            </h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto"
            >
              Desarrollo de software especializado, inteligencia artificial, 
              agentes IA y automatización para la transformación digital de tu empresa.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/contacto"
                className="cta-primary-button"
              >
                <span>Hablar con un Asesor</span>
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            animate={{ y: [-10, 10, -10] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-4 h-4 bg-primary rounded-full opacity-60"
          />
          <motion.div
            animate={{ y: [10, -10, 10] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 right-1/4 w-6 h-6 bg-secondary rounded-full opacity-40"
          />
          <motion.div
            animate={{ y: [-5, 15, -5] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-accent rounded-full opacity-50"
          />
        </div>
      </section>

      {/* Services Preview - Enhanced */}
      <section className="py-20 bg-gradient-to-b from-transparent to-dark/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
              Nuestros Servicios
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Soluciones tecnológicas avanzadas diseñadas para impulsar tu negocio hacia el futuro digital con innovación y excelencia
            </p>
          </motion.div>

          <div className="service-card-grid">
            {featuredServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                onHoverStart={() => setHoveredCard(service.id)}
                onHoverEnd={() => setHoveredCard(null)}
                className={service.featured ? 'service-card-featured-container' : ''}
              >
                <Link to={service.link}>
                  <div className={`service-card ${service.featured ? 'service-card-featured' : ''}`}>
                    <div className="service-card-content">
                      {/* Category Tag */}
                      <div className="flex items-center justify-between">
                        <span className="service-card-category">
                          {service.category}
                        </span>
                        {service.featured && (
                          <span style={{
                            background: 'rgba(15, 246, 252, 0.2)',
                            border: '1px solid rgba(15, 246, 252, 0.5)',
                            color: '#0ff6fc',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px'
                          }}>
                            DESTACADO
                          </span>
                        )}
                      </div>

                      {/* Icon */}
                      <div className="service-card-icon">
                        <motion.div
                          animate={hoveredCard === service.id ? { scale: 1.05 } : { scale: 1 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          <service.icon 
                            className={service.featured ? "w-14 h-14" : "w-12 h-12"} 
                            animate={hoveredCard === service.id}
                          />
                        </motion.div>
                      </div>

                      {/* Title */}
                      <h3 className={`service-card-title ${service.featured ? 'service-card-title-featured' : ''}`}>
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="service-card-description">
                        {service.featured ? service.description : service.shortDesc}
                      </p>

                      {/* CTA Button */}
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="service-card-button">
                          <span>Conocer más</span>
                          <ArrowRightIcon 
                            className="h-4 w-4" 
                            style={{ 
                              transform: hoveredCard === service.id ? 'translateX(4px)' : 'translateX(0)',
                              transition: 'transform 0.3s ease'
                            }} 
                          />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/servicios"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold text-lg group"
            >
              <span>Ver todos los servicios</span>
              <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Enhanced with Direct CSS */}
      <section className="cta-section">
        <div className="cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-card"
          >
            {/* Tech Grid Background */}
            <div className="tech-grid-bg" />
            
            {/* Content */}
            <div className="cta-content">
              {/* Transform Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                whileInView={{ scale: 1, rotate: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
                className="cta-icon"
              >
                <TransformIcon className="w-20 h-20" animate={true} />
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="cta-title"
              >
                ¿Listo para Transformar tu Negocio?
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="cta-subtitle"
              >
                Únete a las empresas que ya han dado el salto hacia la innovación digital y el futuro tecnológico
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="cta-buttons"
              >
                {/* Primary CTA */}
                <motion.button
                  onClick={handleScrollToContact}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cta-button-primary"
                >
                  <span>Comenzar Ahora</span>
                  <ArrowRightIcon className="h-6 w-6" />
                </motion.button>

                {/* Secondary CTA */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cta-button-secondary"
                >
                  <PlayIcon className="h-6 w-6" />
                  <span>Ver Demo</span>
                </motion.button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
                className="cta-trust-indicators"
              >
                <div className="cta-trust-item">
                  <div className="cta-trust-dot cta-trust-dot-1" />
                  <span>Consulta gratuita</span>
                </div>
                <div className="cta-trust-item">
                  <div className="cta-trust-dot cta-trust-dot-2" />
                  <span>Sin compromiso</span>
                </div>
                <div className="cta-trust-item">
                  <div className="cta-trust-dot cta-trust-dot-3" />
                  <span>Respuesta en 24h</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
